import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Spin } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric10,
  type DashboardMetric10CycleOpenGapRiskBreakdown,
} from '@/services/dashboardService';
import { RiskRanking } from '@/services/types';

type ChartRow = {
  cycleId: string;
  cycleCode: string;
  cycleName: string;
  cycleLabel: string; // e.g., "A00 Revenue"
  highCount: number;
  mediumCount: number;
  lowCount: number;
};

const RISK_COLORS = {
  high: '#EF4444', // red
  medium: '#E6A700', // yellow-ish
  low: '#3B82F6', // blue (matches your sample)
};

const Metric10Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const row: ChartRow | undefined = payload?.[0]?.payload;
  if (!row) return null;

  // payload includes entries for each Bar with its value
  const items = payload
    .filter((p: any) => typeof p?.value === 'number' && p.value > 0)
    .map((p: any) => ({
      name: p.name,
      value: p.value,
      color: p.color,
    }));

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{row.cycleLabel}</div>
      {items.length ? (
        items.map((i: any) => (
          <div
            key={i.name}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 4,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: i.color,
                  display: 'inline-block',
                }}
              />
              {i.name}
            </span>
            <span style={{ fontWeight: 700 }}>{i.value}</span>
          </div>
        ))
      ) : (
        <div style={{ color: 'rgba(0,0,0,0.6)' }}>
          No open gaps for these risk levels.
        </div>
      )}
      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a bar to open Exhibit 6 filtered by cycle + risk.
      </div>
    </div>
  );
};

export type EX1OpenGapRankingByCycleAndRiskLevelHorizontalGroupedBarChartProps =
  {
    height?: number;
    title?: string;
  };

export const EX1OpenGapRankingByCycleAndRiskLevelHorizontalGroupedBarChart: React.FC<
  EX1OpenGapRankingByCycleAndRiskLevelHorizontalGroupedBarChartProps
> = ({
  height = 360,
  title = 'Open GAPs Ranking by Cycle',
}) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric10CycleOpenGapRiskBreakdown[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric10(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 10 (Open GAP ranking by cycle & risk).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: ChartRow[] = useMemo(() => {
    return raw.map(x => ({
      cycleId: x.cycleId,
      cycleCode: x.cycleCode,
      cycleName: x.cycleName,
      cycleLabel: `${x.cycleCode} ${x.cycleName}`.trim(),
      highCount: x.highCount ?? 0,
      mediumCount: x.mediumCount ?? 0,
      lowCount: x.lowCount ?? 0,
    }));
  }, [raw]);

  const maxTotal = useMemo(() => {
    // max of (high + medium + low) per row for axis scaling
    return Math.max(
      0,
      ...data.map(
        d => (d.highCount ?? 0),
      ),
      ...data.map(
        d => (d.mediumCount ?? 0),
      ),
      ...data.map(
        d => (d.lowCount ?? 0),
      ),
    );
  }, [data]);

  const xTicks = useMemo(() => {
    // same pattern as your other charts: 1..maxTotal (forces odd + even tick labels)
    return Array.from({ length: maxTotal }, (_, i) => i + 1);
  }, [maxTotal]);

  const riskTotals = useMemo(() => {
    return data.reduce(
      (acc, d) => {
        acc.high += d.highCount ?? 0;
        acc.medium += d.mediumCount ?? 0;
        acc.low += d.lowCount ?? 0;
        return acc;
      },
      { high: 0, medium: 0, low: 0 },
    );
  }, [data]);

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // Navigate to Gaps page with state
  const goToGapsForCycle = (
    cycleId: string,
    cycleCode: string,
    cycleName: string,
  ) => {
    navigate('/gaps', { state: { cycleId, cycleCode, cycleName } });
  };

  const goToGapsForCycleAndRisk = (
    cycleId: string,
    cycleCode: string,
    cycleName: string,
    riskRating: RiskRanking,
  ) => {
    navigate('/gaps', { state: { cycleId, cycleCode, cycleName, riskRating } });
  };

  // clickable Y axis labels
  const YTick = (props: any) => {
    const { x, y, payload } = props;
    const label = payload?.value as string;

    const row = data.find(d => d.cycleLabel === label);

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor='end'
          fill='#111827'
          style={{ cursor: row ? 'pointer' : 'default', fontSize: 12 }}
          onClick={() => {
            if (row)
              goToGapsForCycle(row.cycleId, row.cycleCode, row.cycleName);
          }}
        >
          {label}
        </text>
      </g>
    );
  };

  const computedHeight = useMemo(() => {
    const rowHeight = 55; // tweak 24–36 based on your font/spacing
    const min = height;
    const needed = data.length * rowHeight + 80; // padding for axes/legend
    return Math.max(min, needed);
  }, [data.length, height]);

  return (
    <Card title={title}>
      <div style={{ height: computedHeight }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 10 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No open gaps were returned for this report.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              margin={
                isNarrow
                  ? { top: 8, right: 16, bottom: 40, left: 48 }
                  : { top: 8, right: 140, bottom: 24, left: 48 }
              }
              barCategoryGap={10}
            >
              <XAxis
                type='number'
                domain={[0, maxTotal]}
                ticks={xTicks}
                allowDecimals={false}
                label={{
                  value: 'Count of Open Gaps',
                  position: 'insideBottom',
                  offset: -10,
                }}
              />
              <YAxis
                type='category'
                dataKey='cycleLabel'
                width={180}
                interval={0}
                tickMargin={8}
                tick={<YTick />}
                label={{
                  value: 'Cycle Name',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -25,
                }}
              />

              <Tooltip content={<Metric10Tooltip />} />
              <Legend
                layout={isNarrow ? 'horizontal' : 'vertical'}
                align={isNarrow ? 'center' : 'right'}
                verticalAlign={isNarrow ? 'bottom' : 'middle'}
                wrapperStyle={
                  isNarrow ? { paddingTop: 12 } : { paddingLeft: 26 }
                }
                formatter={(value: string) => {
                  switch (value) {
                    case 'High':
                      return `High (${riskTotals.high})`;
                    case 'Medium':
                      return `Medium (${riskTotals.medium})`;
                    case 'Low':
                      return `Low (${riskTotals.low})`;
                    default:
                      return value;
                  }
                }}
              />

              {/* Grouped bars (NOT stacked) */}
              <Bar
                dataKey='highCount'
                name='High'
                fill={RISK_COLORS.high}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const { cycleId, cycleCode, cycleName } =
                    barData?.payload || {};
                  if (cycleId)
                    goToGapsForCycleAndRisk(
                      cycleId,
                      cycleCode,
                      cycleName,
                      'High',
                    );
                }}
                style={{ cursor: 'pointer' }}
              />

              <Bar
                dataKey='mediumCount'
                name='Medium'
                fill={RISK_COLORS.medium}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const { cycleId, cycleCode, cycleName } =
                    barData?.payload || {};
                  if (cycleId)
                    goToGapsForCycleAndRisk(
                      cycleId,
                      cycleCode,
                      cycleName,
                      'Med',
                    );
                }}
                style={{ cursor: 'pointer' }}
              />

              <Bar
                dataKey='lowCount'
                name='Low'
                fill={RISK_COLORS.low}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const { cycleId, cycleCode, cycleName } =
                    barData?.payload || {};
                  if (cycleId)
                    goToGapsForCycleAndRisk(
                      cycleId,
                      cycleCode,
                      cycleName,
                      'Low',
                    );
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default EX1OpenGapRankingByCycleAndRiskLevelHorizontalGroupedBarChart;
